/**
 * TOSS Web Application - Client portal bottom navigation (phone)
 *
 * The portal's mobile navigation pattern, and the one place where it differs from the
 * internal system by design: `AppLayout` gives a phone a hamburger and a slide-out
 * drawer, the customer wireframes ask for a fixed bottom bar. That difference is the
 * main reason the portal has its own layout instead of a branch inside `AppLayout`
 * (decision Р5/Р6).
 *
 * ---------------------------------------------------------------------------
 * THE BAR IS THE ONE FROM «СМЕТЫ» (26.08.2026, owner's choice of variant A)
 * ---------------------------------------------------------------------------
 *
 * Until this date the bar was flat: the active item differed from the other four by the
 * colour of its icon and label and by nothing else. The owner's own Flutter application
 * solves the same bar with a filled pill, and asked for that pattern here verbatim — the
 * source is `smety/lib/features/main/presentation/widgets/bottom_nav_bar.dart`, outside this
 * repository, and every number below is read off it rather than invented:
 *
 *   - pill 50×30, radius 16, `colors.primary` → the same box on `--color-primary-500`;
 *   - it ARRIVES rather than appears — `AnimatedScale` 0→1 over 250 ms on `easeOutBack`, which
 *     is `cubic-bezier(0.34, 1.56, 0.64, 1)` (already used by `index.css` for its two spring
 *     animations, so the overshoot matches the rest of the application);
 *   - the icon SHRINKS when selected, 20px → 18px, white inside the pill and
 *     `secondaryText` outside it;
 *   - the label is 12px at normal weight in BOTH states. Colour carries the selection, not
 *     weight — this is why the previous `font-medium` on the active item is gone: a bold
 *     label under a filled pill states the same thing twice and makes the row jitter as the
 *     text re-flows;
 *   - the badge is a DOT, not a count, and it moves: `top -4 / right 0` over the pill,
 *     `top 2 / right 14` over a bare icon.
 *
 * **The count survives the dot.** It is gone from the screen but not from the accessibility
 * tree — the `sr-only` sentence below still says how many, and it is the only place a
 * screen-reader user could get the number from.
 *
 * Geometry adds up to the SAME 64px the flat bar occupied — 8 top + 30 pill + 2 + 16 label +
 * 8 bottom — which is what lets `ClientLayout` keep its reservations (`!pb-24` on a scrolling
 * screen, a `64px + safe-area` spacer on a viewport-locked one) untouched.
 *
 * The `env(safe-area-inset-bottom)` padding is the iOS home indicator: without it the bar sits
 * under it on a notched device. `z-[var(--z-sticky)]` and the arbitrary heights are values over
 * variables that really exist in `index.css` — this project runs **Tailwind v4** with CSS-first
 * configuration and no `@config` directive, so `tailwind.config.js` is inert and its
 * `h-bottom-nav` / `z-sticky` / `text-2xs` generate no CSS at all.
 *
 * **Under the keyboard the bar leaves.** `MainShell` in «Сметы» collapses it on
 * `viewInsets.bottom > 0`; the web equivalent is {@link useVirtualKeyboardOpen}, and the reason
 * is the chat: its composer is pinned to the bottom of the window, and a bar that stays put
 * either covers the field being typed into (Android, where the window resizes) or hovers over
 * the keyboard (iOS, where `fixed` resolves against a layout viewport the keyboard does not
 * shrink). The links go out of the tab order with it — `aria-hidden` over reachable controls
 * is a worse defect than the one it fixes.
 *
 * Items and order come from the SAME config as `ClientSidebar` — the bar renders
 * `clientBottomNavItems` and nothing else. Until the mobile rework (2026-08-21) the fifth
 * slot was a synthetic «Ещё» trigger opening `ClientMoreSheet`; it is «Профиль» again, and
 * the four screens that sheet hid are the menu of that screen
 * (`clientProfileHubItems` → `ClientProfilePage`). Which item sits on the bar is a config
 * flag, not a filter written here, so the sidebar and the bar still cannot disagree about
 * what the menu is.
 *
 * **Badges: the bar counts what is BEHIND an item, not only what the item is.**
 * «Сообщения» carries unread chat, «Профиль» carries unread notifications — the feed moved
 * one level down when it became a row of the profile hub, and a count that only appears
 * after you open the screen hiding it is not a signal. This is the bar's own rule and does
 * not reopen the PO decision about the bell, which is about section HEADERS: the bell is
 * still on the dashboard and nowhere else.
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useClientNavBadges } from '@/hooks/api/clientPortal/useClientBadges';
import { useVirtualKeyboardOpen } from '@/hooks/useVirtualKeyboardOpen';
import {
  clientBottomNavItems,
  isClientNavItemActive,
  type ClientNavItem,
} from './client-nav/clientNavigationConfig';

// ============================================================================
// Constants
// ============================================================================

/** `easeOutBack` — the curve the pill arrives on in «Сметы», and `index.css`'s spring curve. */
const SPRING = 'ease-[cubic-bezier(0.34,1.56,0.64,1)]';

// ============================================================================
// Types
// ============================================================================

export interface ClientBottomNavProps extends HTMLAttributes<HTMLElement> {
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Sub-components
// ============================================================================

function ClientBottomNavItem({
  item,
  isActive,
  badge,
  reachable,
}: {
  item: ClientNavItem;
  isActive: boolean;
  badge: number;
  /** `false` while the bar is off-screen under the keyboard — see the module comment. */
  reachable: boolean;
}): JSX.Element {
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      aria-current={isActive ? 'page' : undefined}
      tabIndex={reachable ? undefined : -1}
      className="flex flex-1 flex-col items-center justify-center min-w-0 touch-target"
    >
      {/* The pill's box, 50×30, present in both states: the pill scales inside it, so the icon
          never moves sideways when an item is selected. */}
      <span className="relative flex h-[30px] w-[50px] items-center justify-center">
        <span
          className={`
            absolute inset-0 rounded-[var(--radius-sheet)] bg-[var(--color-primary-500)]
            transition-transform duration-[250ms] ${SPRING} motion-reduce:transition-none
            ${isActive ? 'scale-100' : 'scale-0'}
          `}
          aria-hidden="true"
        />
        <Icon
          className={`
            relative transition-[width,height,color] duration-[250ms] ${SPRING}
            motion-reduce:transition-none
            ${
              isActive
                ? 'h-[18px] w-[18px] text-[var(--color-text-on-primary)]'
                : 'h-5 w-5 text-[var(--color-text-secondary)]'
            }
          `}
        />
        {badge > 0 && (
          <span
            className={`
              absolute h-[11px] w-[11px] rounded-full bg-[var(--color-error)]
              border-[1.5px] border-[var(--color-bg-card)]
              transition-[top,right] duration-[250ms] ease-out motion-reduce:transition-none
              ${isActive ? '-top-1 right-0' : 'top-[2px] right-[14px]'}
            `}
            aria-hidden="true"
          />
        )}
      </span>
      <span
        className={`
          mt-[2px] max-w-full truncate text-xs leading-4 font-normal
          transition-colors duration-150 motion-reduce:transition-none
          ${isActive ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-secondary)]'}
        `}
      >
        {item.label}
      </span>
      {badge > 0 && <span className="sr-only">{`, непрочитанных: ${badge}`}</span>}
    </Link>
  );
}

// ============================================================================
// Component
// ============================================================================

export const ClientBottomNav = forwardRef<HTMLElement, ClientBottomNavProps>(
  ({ className = '', ...props }, ref) => {
    const location = useLocation();
    const { notificationsCount, chatCount } = useClientNavBadges();
    const isKeyboardOpen = useVirtualKeyboardOpen();

    // The mapping is a property of the MENU, not of this bar: an item's badge source must
    // not depend on whether the count belongs to the item itself («Сообщения») or to a
    // screen one tap behind it («Уведомления», now a row of the profile hub).
    const badgeFor = (itemId: string): number => {
      if (itemId === 'client-messenger') return chatCount;
      if (itemId === 'client-profile') return notificationsCount;
      return 0;
    };

    const navClasses = [
      'fixed bottom-0 left-0 right-0',
      // 8 + 30 (pill) + 2 + 16 (label) + 8 = the 64px `ClientLayout` reserves for this bar.
      'pt-2 pb-[calc(8px+env(safe-area-inset-bottom,0px))]',
      // Боковой вырез экрана: в альбомной ориентации крайние пункты («Главная», «Профиль») иначе
      // уезжают под чёлку. У кабинета блокера ландшафта нет — см. `client-safe-x` в `index.css`.
      'client-safe-x',
      // Поверхность, а не канва: с 26.08.2026 канва серая, и панель её цветом слилась бы со
      // списком, который под неё уезжает. Кольцо вокруг счётчика выше — тот же цвет по той же
      // причине: оно вырезает кружок из панели, а не из страницы.
      'bg-[var(--color-bg-card)] border-t border-[var(--color-border)]',
      'flex items-stretch justify-around',
      'z-[var(--z-sticky)]',
      'transition-transform duration-200 ease-out motion-reduce:transition-none',
      isKeyboardOpen ? 'translate-y-full pointer-events-none' : 'translate-y-0',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <nav
        ref={ref}
        aria-label="Основное меню"
        aria-hidden={isKeyboardOpen || undefined}
        className={navClasses}
        {...props}
      >
        {clientBottomNavItems.map((item) => (
          <ClientBottomNavItem
            key={item.id}
            item={item}
            isActive={isClientNavItemActive(item, location.pathname)}
            badge={badgeFor(item.id)}
            reachable={!isKeyboardOpen}
          />
        ))}
      </nav>
    );
  }
);

ClientBottomNav.displayName = 'ClientBottomNav';

export default ClientBottomNav;
